import { Host, Component, Prop, Event, Listen, Watch, State, h, EventEmitter, AttachInternals } from '@stencil/core';
import { EventDetail, generateGuid } from '../../utils';

@Component({
  tag: 'anchor-quantity',
  styleUrl: 'quantity.scss',
  shadow: true,
  formAssociated: true,
})
export class AnchorQuantity {
  @AttachInternals() internals: ElementInternals;

  inputElm!: HTMLAnchorInputElement;

  anchorButtonIncrement!: HTMLAnchorIconButtonElement;

  anchorButtonDecrement!: HTMLAnchorIconButtonElement;

  @State() isChanged: boolean = false;

  @Watch('value')
  valueChanged() {
    this.isChanged = true;
  }

  /**
   * The value in the input field. Use this to set a value
   */
  @Prop({ mutable: true, reflect: true, }) value: number = 0;

  /**
   * The minimum value of the input field
   */
  @Prop({ mutable: true, reflect: true, }) min?: number = undefined;

  /**
   * The maximum value of the input field
   */
  @Prop({ mutable: true, reflect: true, }) max?: number = undefined;

  /**
   * The step value of the input field
   */
  @Prop({ mutable: true, reflect: true, }) step?: number = 1;

  /**
   * The id of the input field
   */
  @Prop() inputId: string = `anchor-quantity-${generateGuid()}`;

  /**
   * Disable the input field
   */
  @Prop({ mutable: true, reflect: true, }) disabled: boolean = false;

  /**
   * Set the field to be read only
   */
  @Prop({ mutable: true, reflect: true, }) readonly: boolean = false;

  /**
   * Show an error state on input field
   */
  @Prop({ mutable: true, reflect: true, }) error: boolean = false;

  /**
   * Show a label above the input field
   */
  @Prop() label?: string;

  /**
   * Show an error message
   */
  @Prop() errorText?: string;

  /**
   * Show a help text
   */
  @Prop() helpText?: string;

  /**
   * aria-live attribute for the input field
   */
  @Prop() ariaLive: string;

  /**
   * aria-describedby attribute for the input field
   */
  @Prop() ariaDescribedBy: string = '';

  /**
   * aria-activedescendant attribute for the input field
   */
  @Prop() ariaActivedescendant: string = '';

  /**
   * Event emitted when the user is inputting something into the input field
   */
  @Event() inputevent: EventEmitter<EventDetail>;

  /**
   * Event emitted when the user is focusing the input field
   */
  @Event() focusevent: EventEmitter;

  /**
   * Event emitted when the user blurs the input field
   */
  @Event() blurevent: EventEmitter;

  /**
   * Event emitted when the user clears the input field
   */
  @Event() clearevent: EventEmitter;

  /**
   *  Event emitted when the input is changed. Only triggers when focus leaves the input
  */
  @Event() inputChange: EventEmitter<EventDetail>;

  keyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
    }

    // ArrowUp and ArrowDown increment and decrement the value
    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') this.anchorButtonIncrement.click();
    if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') this.anchorButtonDecrement.click();

    // Home and End set the value to the min and max
    if (this.min !== undefined && event.key === 'Home') this.value = this.min;
    if (this.max !== undefined && event.key === 'End') this.value = this.max;
  }

  triggerInputEvent = () => {
    // setTimeout handles next tick, so the value is updated before the event is emitted
    setTimeout(() => {
      this.inputElm.shadowRoot.querySelector('input').dispatchEvent(new InputEvent('input', { bubbles: true, cancelable: false, inputType: 'insertText', composed: true, isComposing: false }));
    }, 10);
  }

  @Listen('blur')
  handleBlur() {
    if (this.isChanged)
      this.inputElm.shadowRoot.querySelector('input').dispatchEvent(new InputEvent('change', { bubbles: true, cancelable: false, inputType: 'insertText', composed: true, isComposing: false }));
    this.isChanged = false;
  }

  render() {

    return (
      <Host>
        <div class={`anchor-c-quantity ${this.error || this.errorText ? 'error': ''}`}>
          <anchor-label for={this.inputId}>{this.label ? this.label : <slot name="label" />}</anchor-label>
          {(this.helpText || this.errorText) && (
            <span id={`${this.inputId}__messages`} class={`anchor-c-quantity__messages`}>
              {this.helpText ? <span class="anchor-c-quantity__help-msg">{this.helpText}</span> : ''}
              {this.errorText ? <span class="anchor-c-quantity__error-msg">
                {this.errorText}
                <anchor-icon color="inherit" size="small" name="alert-circle"></anchor-icon>
              </span>: ''}
            </span>
          )}
          <div class={`anchor-c-quantity__container`}>
            <anchor-icon-button
              disabled={this.disabled || this.readonly || this.value === this.min}
              onClick={() => {
                if (typeof this.min === 'undefined' || this.value > this.min) {
                  this.anchorButtonDecrement.classList.add('clicked')
                  this.value -= 1;
                  this.triggerInputEvent();
                  setTimeout(() => {
                    this.anchorButtonDecrement.classList.remove('clicked')
                  }, 100)
                }
              }}
              ref={(el) => {this.anchorButtonDecrement = el as HTMLAnchorIconButtonElement;}}
              ariaLabel="Minus"
            >
              <anchor-icon slot="icon" name="minus"></anchor-icon>
            </anchor-icon-button>
            <anchor-input
              ref={el => {
                this.inputElm = el as HTMLAnchorInputElement;
              }}
              value={this.value}
              disabled={this.disabled}
              readonly={this.readonly}
              errorField={this.error || Boolean(this.errorText)}
              min={this.min}
              max={this.max}
              step={this.step}
              hideSpinButtons={true}
              showLabel={false}
              inputmode="numeric"
              type="number"
              inputId={this.inputId}
              onKeyDown={this.keyDownHandler}
              ariaLive={this.ariaLive}
              ariaDescribedBy={this.ariaDescribedBy}
              ariaActivedescendant={this.ariaActivedescendant}
            ></anchor-input>
            <anchor-icon-button
              disabled={this.disabled || this.readonly || this.value === this.max}
              onClick={() => {
                if (typeof this.max === 'undefined' || this.value < this.max) {
                  this.anchorButtonIncrement.classList.add('clicked')
                  this.value += 1;
                  this.triggerInputEvent();
                  setTimeout(() => {
                    this.anchorButtonIncrement.classList.remove('clicked')
                  }, 100)
                }
              }}
              ref={(el) => {this.anchorButtonIncrement = el as HTMLAnchorIconButtonElement;}}
              ariaLabel="Plus"
            >
              <anchor-icon slot="icon" name="plus"></anchor-icon>
            </anchor-icon-button>
          </div>
        </div>
      </Host>
    );
  }
}
